import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useSearchParams } from "react-router";
import { resetPasswordSchema, type ResetPasswordInput } from "@theadam/shared";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/form";
import { Alert } from "@/components/ui/misc";
import { ApiError, api } from "@/lib/api";
import { AuthLayout } from "./AuthLayout";

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const form = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { token, password: "", passwordConfirm: "" },
  });
  const { errors, isSubmitting } = form.formState;

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await api.post("/auth/reset-password", values);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Şifre güncellenemedi");
    }
  });

  if (!token) {
    return (
      <AuthLayout title="Bağlantı geçersiz">
        <Alert tone="warning" title="Sıfırlama bağlantısı eksik veya hatalı.">
          <Link className="underline" to="/sifremi-unuttum">
            Yeni bir bağlantı isteyin
          </Link>
        </Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Yeni şifre belirleyin" subtitle="En az 12 karakter; büyük harf, küçük harf ve rakam içermeli.">
      {done ? (
        <div className="space-y-6">
          <Alert tone="success" title="Şifreniz güncellendi">
            Güvenliğiniz için tüm oturumlarınız kapatıldı.
          </Alert>
          <Button asChild className="w-full">
            <Link to="/giris">Giriş yap</Link>
          </Button>
        </div>
      ) : (
        <form onSubmit={onSubmit} noValidate className="space-y-5">
          {error && <Alert tone="danger" title={error} />}
          <Field label="Yeni şifre" error={errors.password?.message}>
            {(p) => <Input {...p} type="password" autoComplete="new-password" autoFocus {...form.register("password")} />}
          </Field>
          <Field label="Yeni şifre (tekrar)" error={errors.passwordConfirm?.message}>
            {(p) => <Input {...p} type="password" autoComplete="new-password" {...form.register("passwordConfirm")} />}
          </Field>
          <Button type="submit" className="w-full" size="lg" loading={isSubmitting}>
            Şifreyi güncelle
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
